// The Fit and Engagement level chips (go-live), shared by All accounts and the board (My Accounts /
// Strategist Accounts): which rows a pick keeps, and each chip's live count.
//
// Levels in one kind are OR-ed (Fit high or mid); Fit and Engagement are AND-ed. A kind's counts
// are over the rows the other kind keeps, so each says how many rows clicking it would show. Pass
// rows the page's other filters have already narrowed. The levels only count while the score
// columns are drawn (`scores_visible`); hidden, nothing is filtered and nothing is counted.
//
// Pure, and imported by `node --test`.

import type { BookAccount } from '../api/types';
import { scoreLevel, scoreOf } from './scores.ts';
import type { ScoreLevel } from './scores.ts';

export type ScoreKind = 'fit' | 'engagement';

export const LEVELS: ScoreLevel[] = ['high', 'mid', 'low'];
export const LEVEL_LABEL: Record<ScoreLevel, string> = { high: 'High', mid: 'Mid', low: 'Low' };

export interface ScoreLevelFilters {
  fit_levels: ScoreLevel[];
  engagement_levels: ScoreLevel[];
}

export interface ScoreLevelCounts {
  fit_levels: Record<ScoreLevel, number>;
  engagement_levels: Record<ScoreLevel, number>;
}

export function emptyScoreLevels(): ScoreLevelFilters {
  return { fit_levels: [], engagement_levels: [] };
}

export function levelOf(row: BookAccount, kind: ScoreKind): ScoreLevel | null {
  const score = scoreOf(row);
  const value = kind === 'fit' ? score?.fit_score : score?.engagement_score;
  return scoreLevel(kind, value);
}

// One kind's pick. An unscored row never matches a level.
export function passesScoreKind(row: BookAccount, kind: ScoreKind, wanted: ScoreLevel[], scoresShown: boolean): boolean {
  if (!scoresShown || wanted.length === 0) return true;
  const level = levelOf(row, kind);
  return level != null && wanted.includes(level);
}

export function passesScoreLevels(row: BookAccount, levels: ScoreLevelFilters, scoresShown: boolean): boolean {
  return passesScoreKind(row, 'fit', levels.fit_levels, scoresShown)
    && passesScoreKind(row, 'engagement', levels.engagement_levels, scoresShown);
}

export function applyScoreLevels<Row extends BookAccount>(rows: Row[], levels: ScoreLevelFilters, scoresShown: boolean): Row[] {
  if (!scoresShown) return rows;
  return rows.filter(row => passesScoreLevels(row, levels, scoresShown));
}

export function emptyLevelCounts(): ScoreLevelCounts {
  return { fit_levels: { high: 0, mid: 0, low: 0 }, engagement_levels: { high: 0, mid: 0, low: 0 } };
}

// Each chip's count: Fit over the rows Engagement keeps, Engagement over the rows Fit keeps.
export function scoreLevelCounts(rows: BookAccount[], levels: ScoreLevelFilters, scoresShown: boolean): ScoreLevelCounts {
  const counts = emptyLevelCounts();
  if (!scoresShown) return counts;

  for (const row of rows) {
    // Fit's chips, past the Engagement pick.
    if (passesScoreKind(row, 'engagement', levels.engagement_levels, scoresShown)) {
      const fitLevel = levelOf(row, 'fit');
      if (fitLevel) counts.fit_levels[fitLevel] += 1;
    }

    // Engagement's chips, past the Fit pick.
    if (passesScoreKind(row, 'fit', levels.fit_levels, scoresShown)) {
      const engagementLevel = levelOf(row, 'engagement');
      if (engagementLevel) counts.engagement_levels[engagementLevel] += 1;
    }
  }
  return counts;
}

// How many level chips are on, for a Clear all. None count while the scores are hidden.
export function scoreLevelActiveCount(levels: ScoreLevelFilters, scoresShown: boolean): number {
  if (!scoresShown) return 0;
  return levels.fit_levels.length + levels.engagement_levels.length;
}
